/**
 * Who may sign in to the admin site, and who may edit that list.
 *
 * The list lives in the content bucket so the Cognito pre-sign-up trigger and
 * the admin API read the same copy. The API re-reads it on every request, so
 * revoking an account takes effect before the caller's token expires.
 */

import { isJsonObject } from "./json.ts";

export interface AllowlistEntry {
  /** Lower-cased. Compare with `normalizeEmail`, never raw. */
  email: string;
  /** Admins may edit the allowlist. Everyone else only manages content. */
  admin: boolean;
}

export interface Allowlist {
  entries: AllowlistEntry[];
}

/** Cognito and Google disagree on case; the list is the one that decides. */
/**
 * Why a sign-up was turned away. Shared because both ends need the same words:
 * the pre-sign-up trigger throws one, and Cognito hands it to the browser
 * inside its own sentence, where the sign-in screen matches on it to show
 * something a person can act on.
 *
 * Neither ends with a full stop. Cognito appends one, and a message that
 * brought its own reaches the browser ending in "..".
 */
export const REFUSED_NOT_ALLOWED = "This account is not on the phone booth allowlist";
export const REFUSED_NO_EMAIL = "This sign-in provided no email address";

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Loose on purpose: the real check is whether the address is on the list. */
export function isValidEmail(email: string): boolean {
  return (
    email.length <= 320 && /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(normalizeEmail(email))
  );
}

function fail(what: string): never {
  throw new Error(`malformed allowlist: ${what}`);
}

export function parseAllowlist(value: unknown): Allowlist {
  if (!isJsonObject(value)) fail("not an object");
  const raw = value.entries;
  if (!Array.isArray(raw)) fail("entries is not an array");
  const entries: AllowlistEntry[] = [];
  for (const entry of raw) {
    if (!isJsonObject(entry)) fail("an entry is not an object");
    if (typeof entry.email !== "string" || !isValidEmail(entry.email)) {
      fail(`"${String(entry.email)}" is not an email address`);
    }
    if (typeof entry.admin !== "boolean") fail("admin is not a boolean");
    const email = normalizeEmail(entry.email);
    if (entries.some((existing) => existing.email === email)) {
      fail(`${email} is listed twice`);
    }
    entries.push({ email, admin: entry.admin });
  }
  return { entries };
}

export function findEntry(list: Allowlist, email: string): AllowlistEntry | null {
  const wanted = normalizeEmail(email);
  return list.entries.find((entry) => entry.email === wanted) ?? null;
}

export function isAllowed(list: Allowlist, email: string): boolean {
  return findEntry(list, email) !== null;
}

export function adminCount(list: Allowlist): number {
  return list.entries.filter((entry) => entry.admin).length;
}

/** Sorted so the stored file and the UI both read alphabetically. */
function sorted(entries: AllowlistEntry[]): AllowlistEntry[] {
  return [...entries].sort((a, b) => a.email.localeCompare(b.email));
}

/** Adds the address, or changes the admin flag on one already listed. */
export function upsertEntry(list: Allowlist, entry: AllowlistEntry): Allowlist {
  const email = normalizeEmail(entry.email);
  if (!isValidEmail(email)) throw new Error(`"${entry.email}" is not an email address`);
  const others = list.entries.filter((existing) => existing.email !== email);
  return { entries: sorted([...others, { email, admin: entry.admin }]) };
}

export function removeEntry(list: Allowlist, email: string): Allowlist {
  const wanted = normalizeEmail(email);
  return { entries: sorted(list.entries.filter((entry) => entry.email !== wanted)) };
}

/**
 * The list a deploy starts from when the bucket holds none. Seeding on first
 * read rather than writing at deploy time keeps the stored list the only thing
 * that decides access: once it exists, the seed is never consulted again, so
 * the first admin can be removed like anyone else.
 */
export function seedAllowlist(email: string | undefined): Allowlist {
  if (!email || !isValidEmail(email)) return { entries: [] };
  return { entries: [{ email: normalizeEmail(email), admin: true }] };
}
